import { useCallback, useSyncExternalStore } from 'react';

/**
 * Feed filter preferences, persisted to localStorage and shared across all
 * components via a tiny external store (so toggling a filter in one place
 * updates every mounted feed instantly).
 */

export interface VideoFeedFilter {
  /** Show videos that have at least one kind 1984 report. Default: false. */
  showReported: boolean;
  /** Show videos whose media URL is a YouTube link. Default: true. */
  showYouTube: boolean;
  /** Show videos whose duration could not be determined. Default: true. */
  showUnknownDuration: boolean;
  /** Show kind 1 notes with linked video files (the "Notes" tab). Default: true. */
  showNoteVideos: boolean;
}

const STORAGE_KEY = 'vidstr:feed-filter';

const DEFAULT_FILTER: VideoFeedFilter = {
  showReported: false,
  showYouTube: true,
  showUnknownDuration: true,
  showNoteVideos: true,
};

let current: VideoFeedFilter = load();
const listeners = new Set<() => void>();

function load(): VideoFeedFilter {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_FILTER;
    const parsed = JSON.parse(raw) as Partial<VideoFeedFilter>;
    return { ...DEFAULT_FILTER, ...parsed };
  } catch {
    return DEFAULT_FILTER;
  }
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch { /* ignore */ }
}

function setFilter(update: Partial<VideoFeedFilter>) {
  current = { ...current, ...update };
  persist();
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function useVideoFeedFilter() {
  const filter = useSyncExternalStore(subscribe, () => current);

  const update = useCallback((updateValues: Partial<VideoFeedFilter>) => {
    setFilter(updateValues);
  }, []);

  return { filter, updateFilter: update };
}
